/** @jsxImportSource @emotion/react */
import React from "react";
import { Select, Button, Tag, Typography, Row, Col, message } from "antd";
import * as Styles from "./styles";

export const ANY_OPTION = { label: "Any", value: "<Any>" };

export interface KeyValuePair {
  key: string;
  value: string;
}

export interface KeyValueInputProps {
  label: string;
  sourceKeyValues: Record<string, string[]>;
  pairs: KeyValuePair[];
  hasAnyValue?: boolean;
  freeformKeys?: string[];
  onAdd: (pair: KeyValuePair) => void;
  onRemove: (index: number) => void;
}

export const KeyValueInput: React.FC<KeyValueInputProps> = ({
  label,
  sourceKeyValues,
  pairs,
  hasAnyValue = true,
  freeformKeys = [],
  onAdd,
  onRemove,
}: KeyValueInputProps) => {
  const [draftKey, setDraftKey] = React.useState("");
  const [draftValue, setDraftValue] = React.useState("");
  const [valueSearch, setValueSearch] = React.useState("");

  // List conditions for filter by and tag value select options:
  // 1. Any option
  // 2. Fixed values from source
  // 3. If freeform key and typed value is not in fixed values, add typed value as an extra option
  const isFreeformValue = freeformKeys.includes(draftKey);
  const fixedValues = Array.from(new Set(sourceKeyValues[draftKey] || []));
  const typedValue = valueSearch.trim();
  const extraOption =
    isFreeformValue && typedValue && !fixedValues.includes(typedValue)
      ? [{ label: typedValue, value: typedValue }]
      : [];
  const valueOptions = [];
  if (hasAnyValue) {
    valueOptions.push(ANY_OPTION);
  }
  valueOptions.push(
    ...fixedValues.map((value) => ({ label: value, value })),
    ...extraOption,
  );

  const handleAdd = () => {
    if (!draftKey.trim()) {
      message.warning("Key is required");
      return;
    }
    onAdd({ key: draftKey.trim(), value: draftValue.trim() });
    setDraftKey("");
    setDraftValue("");
    setValueSearch("");
  };

  const handleKeyChange = (value: string) => {
    setDraftKey(value);
    setDraftValue("");
    setValueSearch("");
  };

  const handleValueChange = (value: string) => {
    setDraftValue(value);
  };

  const keyOptions = Object.keys(sourceKeyValues).map((key) => {
    return { label: key, value: key };
  });

  const tagListSection =
    pairs.length > 0 ? (
      <div css={Styles.tagListStyle}>
        {pairs.map((pair, index) => {
          const handleClose = (e: React.MouseEvent<HTMLElement>) => {
            e.preventDefault();
            onRemove(index);
          };
          return (
            <Tag
              key={`${pair.key}-${index}`}
              closable
              onClose={handleClose}
              color="blue"
            >
              {pair.key}:{" "}
              {pair.value === ANY_OPTION.value ? ANY_OPTION.label : pair.value}
            </Tag>
          );
        })}
      </div>
    ) : null;

  return (
    <div css={Styles.sectionStyle}>
      <Row gutter={8} align="middle">
        <Col span={6}>
          <Typography.Text type="secondary">{label}</Typography.Text>
        </Col>
        <Col span={7}>
          <Select
            css={Styles.fullWidthStyle}
            showSearch
            optionFilterProp="label"
            placeholder="Key"
            value={draftKey || undefined}
            onChange={handleKeyChange}
            options={keyOptions}
          />
        </Col>
        <Col span={7}>
          <Select
            css={Styles.fullWidthStyle}
            showSearch
            optionFilterProp="label"
            placeholder="Value"
            value={draftValue || undefined}
            onChange={handleValueChange}
            onSearch={isFreeformValue ? setValueSearch : undefined}
            disabled={!draftKey}
            options={valueOptions}
          />
        </Col>
        <Col span={4}>
          <Button onClick={handleAdd}>ADD</Button>
        </Col>
      </Row>
      {tagListSection}
    </div>
  );
};
